// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';

/** 迷雾格三态：未知 / 已探索 / 当前可见。 */
export type FogState = 0 | 1 | 2;

export interface FogObstacleSpec {
  x: number;
  z: number;
  halfW: number;
  halfD: number;
  /** 弧度，与 ObstacleEntity.rotation 同一约定（绕 Y 轴）。 */
  rotation: number;
  blocksSight: boolean;
}

export interface FogViewer {
  x: number;
  z: number;
  /** 朝向（弧度），世界角约定与坦克一致：atan2(dx, -dz)，0 朝 -Z。扇形以它为中轴。 */
  angle: number;
  /** 该观察者的视野半径（世界单位），缺省用模块默认值。 */
  sight?: number;
  /** 扇形半角（弧度），缺省 65°；扇形外即盲区（贴身小圈除外）。 */
  halfAngle?: number;
}

interface FogOfWarOptions {
  /** 场地宽（世界单位，X 轴）。 */
  width: number;
  /** 场地深（世界单位，Z 轴）。 */
  depth: number;
  cellSize?: number;
  sightRadius?: number;
}

// 未知区域底色（深蓝黑），与设计文档 §6.2 一致。
const UNKNOWN_COLOR = '8,10,18';
const UNKNOWN_ALPHA = 0.92;
const EXPLORED_ALPHA = 0.45;
// destination-out 把 0.92 擦到 0.45 所需的画笔 alpha：0.92 * (1 - k) = 0.45。
const EXPLORED_ERASE_ALPHA = 1 - EXPLORED_ALPHA / UNKNOWN_ALPHA;
/** 扇形视野的默认半角（65°，FPS 式正前方视野）。 */
const DEFAULT_HALF_ANGLE = (65 * Math.PI) / 180;
/** 贴身小圈半径（世界单位）：圈内不论朝向恒可见，避免自己脚下格闪烁。 */
const CLOSE_RANGE = 1.2;
/** DDA 擦墙角 tie 的浮点比较容差。 */
const LOS_EPSILON = 1e-9;

/**
 * 战争迷雾（设计文档 §6）。
 *
 * 覆盖以世界原点为中心 width×depth 区域的三态网格；可见性由
 * 「距离 ≤ 视野半径 且落在朝向扇形内（贴身小圈除外） 且 LOS（网格 DDA）不被阻挡」
 * 决定。遮挡物在初始化时由 setObstacles 烘培进 blockedGrid，Crate 摧毁后用
 * unblockAt 清阻挡。渲染上是一张贴地的 CanvasTexture overlay 平面，逐格擦除，
 * 与 isVisible 判定严格一致；syncOverlay 内部脏检查，仅网格变化时重绘。
 */
export class FogOfWar {
  private readonly width: number;
  private readonly depth: number;
  private readonly cellSize: number;
  private readonly defaultSight: number;
  private readonly cols: number;
  private readonly rows: number;

  /** 三态可见性网格，索引 = gz * cols + gx。 */
  private readonly grid: Uint8Array;
  /** 视线阻挡网格（0/1），与可见性分离，Crate 摧毁只改这里。 */
  private readonly blockedGrid: Uint8Array;

  // overlay 渲染状态（attachOverlay 后才有）。
  private parent: any = null;
  private mesh: any = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private texture: any = null;
  private geometry: any = null;
  private material: any = null;
  private overlayY = 0.02;
  private dirty = true;

  constructor(opts: FogOfWarOptions) {
    this.width = opts.width;
    this.depth = opts.depth;
    this.cellSize = opts.cellSize ?? 1;
    this.defaultSight = opts.sightRadius ?? 9;
    this.cols = Math.max(1, Math.ceil(this.width / this.cellSize));
    this.rows = Math.max(1, Math.ceil(this.depth / this.cellSize));
    this.grid = new Uint8Array(this.cols * this.rows);
    this.blockedGrid = new Uint8Array(this.cols * this.rows);
  }

  /** 烘培障碍：旋转矩形覆盖的格子标记为挡视线（blocksSight=false 的跳过）。 */
  setObstacles(obstacles: FogObstacleSpec[]): void {
    for (const obstacle of obstacles) {
      if (!obstacle.blocksSight) continue;
      this.forEachCoveredCell(obstacle, (index) => {
        this.blockedGrid[index] = 1;
      });
    }
    this.dirty = true;
  }

  /**
   * 清除世界坐标 (x, z) 所在格的阻挡（Crate 摧毁时调用）。
   * 注意：Crate 体积大于一格时，集成方应对其覆盖的每格各调用一次，
   * 或直接使用批量接口 unblockArea（与 setObstacles 同矩形参数）。
   */
  unblockAt(x: number, z: number): void {
    const index = this.cellIndexAt(x, z);
    if (this.blockedGrid[index] !== 0) {
      this.blockedGrid[index] = 0;
      this.dirty = true;
    }
  }

  /** 批量清阻挡：清除旋转矩形覆盖的所有格（Crate 摧毁的推荐用法）。 */
  unblockArea(obstacle: Omit<FogObstacleSpec, 'blocksSight'>): void {
    this.forEachCoveredCell(obstacle, (index) => {
      this.blockedGrid[index] = 0;
    });
    this.dirty = true;
  }

  /**
   * 重算可见集（建议 10Hz 调用，viewers = 本方存活坦克，携带朝向）。
   * 先把所有「当前可见」降级为「已探索」，再逐观察者判定：
   * 格子可见 ⟺ 距离 ≤ 视野半径 且（贴身小圈内 或 与朝向角差 ≤ 扇形半角）且 LOS 不被挡。
   */
  update(viewers: FogViewer[]): void {
    let changed = false;
    for (let i = 0; i < this.grid.length; i++) {
      if (this.grid[i] === 2) {
        this.grid[i] = 1;
        changed = true;
      }
    }

    for (const viewer of viewers) {
      const sight = viewer.sight ?? this.defaultSight;
      const halfAngle = viewer.halfAngle ?? DEFAULT_HALF_ANGLE;
      const sightSq = sight * sight;
      const viewerGx = this.gridXAt(viewer.x);
      const viewerGz = this.gridZAt(viewer.z);

      // 视野半径的格子包围盒，钳制到网格内。
      const cellRadius = Math.ceil(sight / this.cellSize);
      const minGx = Math.max(0, viewerGx - cellRadius);
      const maxGx = Math.min(this.cols - 1, viewerGx + cellRadius);
      const minGz = Math.max(0, viewerGz - cellRadius);
      const maxGz = Math.min(this.rows - 1, viewerGz + cellRadius);

      for (let gz = minGz; gz <= maxGz; gz++) {
        for (let gx = minGx; gx <= maxGx; gx++) {
          const dx = this.cellCenterX(gx) - viewer.x;
          const dz = this.cellCenterZ(gz) - viewer.z;
          const distSq = dx * dx + dz * dz;
          if (distSq > sightSq) continue;
          // 贴身小圈（自己脚下格）不论朝向恒可见；圈外必须在朝向扇形内。
          if (distSq > CLOSE_RANGE * CLOSE_RANGE) {
            const cellAngle = Math.atan2(dx, -dz);
            if (Math.abs(angleDelta(cellAngle, viewer.angle)) > halfAngle) continue;
          }
          if (!this.hasLineOfSight(viewerGx, viewerGz, gx, gz)) continue;
          const index = gz * this.cols + gx;
          if (this.grid[index] !== 2) {
            this.grid[index] = 2;
            changed = true;
          }
        }
      }
    }
    if (changed) this.dirty = true;
  }

  /** 查询世界坐标 (x, z) 处格子的三态（越界钳制到边缘格）。 */
  visibilityAt(x: number, z: number): FogState {
    return this.grid[this.cellIndexAt(x, z)] as FogState;
  }

  isVisible(x: number, z: number): boolean {
    return this.grid[this.cellIndexAt(x, z)] === 2;
  }

  isExplored(x: number, z: number): boolean {
    return this.grid[this.cellIndexAt(x, z)] >= 1;
  }

  /**
   * 一次性把半径内的格子标为「已探索」（不做 LOS 判定）。
   * 用于开局点亮本方出生区域，避免开场全黑的压抑感。
   */
  exploreAt(x: number, z: number, radius: number): void {
    const centerGx = this.gridXAt(x);
    const centerGz = this.gridZAt(z);
    const cellRadius = Math.ceil(radius / this.cellSize);
    const radiusSq = radius * radius;
    for (let gz = Math.max(0, centerGz - cellRadius); gz <= Math.min(this.rows - 1, centerGz + cellRadius); gz++) {
      for (let gx = Math.max(0, centerGx - cellRadius); gx <= Math.min(this.cols - 1, centerGx + cellRadius); gx++) {
        const dx = this.cellCenterX(gx) - x;
        const dz = this.cellCenterZ(gz) - z;
        if (dx * dx + dz * dz > radiusSq) continue;
        const index = gz * this.cols + gx;
        if (this.grid[index] === 0) this.grid[index] = 1;
      }
    }
    this.dirty = true;
  }

  /** 全图揭开（本方全队阵亡、回合结束时用）。 */
  revealAll(): void {
    this.grid.fill(2);
    this.dirty = true;
  }

  /** 清空回全未知（阻挡网格保留，重开一局时按需重新 setObstacles）。 */
  reset(): void {
    this.grid.fill(0);
    this.dirty = true;
  }

  /**
   * 创建并挂接 overlay 平面：与场地同尺寸、贴地（y 抬高防 z-fight），
   * CanvasTexture 尺寸 = 网格宽高，LinearFilter 放大获得柔和边缘。
   */
  attachOverlay(parent: any, yOffset = 0.02): void {
    this.disposeOverlay();
    this.parent = parent;
    this.overlayY = yOffset;

    this.canvas = document.createElement('canvas');
    this.canvas.width = this.cols;
    this.canvas.height = this.rows;
    this.ctx = this.canvas.getContext('2d');

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;

    this.geometry = new THREE.PlaneGeometry(this.width, this.depth);
    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.y = this.overlayY;
    // 迷雾要盖在地表与拾取物之上、尽量靠后绘制。
    this.mesh.renderOrder = 20;
    this.parent.add(this.mesh);
    this.dirty = true;
    this.syncOverlay();
  }

  /** 重绘 overlay 纹理；网格未变化时直接返回（内部脏检查）。 */
  syncOverlay(): void {
    if (!this.dirty || !this.ctx || !this.canvas || !this.texture) return;
    const ctx = this.ctx;

    // 1. 整体填未知底色。
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = `rgba(${UNKNOWN_COLOR},${UNKNOWN_ALPHA})`;
    ctx.fillRect(0, 0, this.cols, this.rows);

    // 2. 已探索格用 destination-out 把 alpha 从 0.92 擦到 0.45。
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = `rgba(0,0,0,${EXPLORED_ERASE_ALPHA})`;
    for (let gz = 0; gz < this.rows; gz++) {
      for (let gx = 0; gx < this.cols; gx++) {
        if (this.grid[gz * this.cols + gx] >= 1) {
          ctx.fillRect(gx, gz, 1, 1);
        }
      }
    }

    // 3. 当前可见格（grid==2）逐格完全擦除，与 isVisible 判定严格一致；
    //    不再按观察者画整圆渐变——LOS 遮挡格与扇形外区域必须保持被盖住。
    ctx.fillStyle = 'rgba(0,0,0,1)';
    for (let gz = 0; gz < this.rows; gz++) {
      for (let gx = 0; gx < this.cols; gx++) {
        if (this.grid[gz * this.cols + gx] === 2) {
          ctx.fillRect(gx, gz, 1, 1);
        }
      }
    }

    ctx.globalCompositeOperation = 'source-over';
    this.texture.needsUpdate = true;
    this.dirty = false;
  }

  /** 释放 canvas/texture/geometry/material 并从 parent 摘除。 */
  dispose(): void {
    this.disposeOverlay();
  }

  private disposeOverlay(): void {
    if (this.mesh && this.parent) {
      this.parent.remove(this.mesh);
    }
    if (this.texture) this.texture.dispose();
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
    this.mesh = null;
    this.texture = null;
    this.geometry = null;
    this.material = null;
    this.canvas = null;
    this.ctx = null;
    this.parent = null;
  }

  /** 世界 X → 格列（钳制）。 */
  private gridXAt(x: number): number {
    const gx = Math.floor((x + this.width / 2) / this.cellSize);
    return Math.min(this.cols - 1, Math.max(0, gx));
  }

  /** 世界 Z → 格行（钳制）。 */
  private gridZAt(z: number): number {
    const gz = Math.floor((z + this.depth / 2) / this.cellSize);
    return Math.min(this.rows - 1, Math.max(0, gz));
  }

  private cellIndexAt(x: number, z: number): number {
    return this.gridZAt(z) * this.cols + this.gridXAt(x);
  }

  private cellCenterX(gx: number): number {
    return (gx + 0.5) * this.cellSize - this.width / 2;
  }

  private cellCenterZ(gz: number): number {
    return (gz + 0.5) * this.cellSize - this.depth / 2;
  }

  /**
   * 遍历旋转矩形覆盖的格子（格中心落入矩形即算覆盖）。
   * 局部坐标换算与 TankGame 中障碍碰撞的约定一致。
   */
  private forEachCoveredCell(
    obstacle: { x: number; z: number; halfW: number; halfD: number; rotation: number },
    visit: (index: number) => void,
  ): void {
    const cos = Math.cos(-obstacle.rotation);
    const sin = Math.sin(-obstacle.rotation);
    // 旋转矩形的世界包围盒，钳制到网格内，避免全网格扫描。
    const extentX = Math.abs(obstacle.halfW * Math.cos(obstacle.rotation)) + Math.abs(obstacle.halfD * Math.sin(obstacle.rotation));
    const extentZ = Math.abs(obstacle.halfW * Math.sin(obstacle.rotation)) + Math.abs(obstacle.halfD * Math.cos(obstacle.rotation));
    const minGx = this.gridXAt(obstacle.x - extentX);
    const maxGx = this.gridXAt(obstacle.x + extentX);
    const minGz = this.gridZAt(obstacle.z - extentZ);
    const maxGz = this.gridZAt(obstacle.z + extentZ);

    for (let gz = minGz; gz <= maxGz; gz++) {
      for (let gx = minGx; gx <= maxGx; gx++) {
        const dx = this.cellCenterX(gx) - obstacle.x;
        const dz = this.cellCenterZ(gz) - obstacle.z;
        const localX = dx * cos - dz * sin;
        const localZ = dx * sin + dz * cos;
        if (Math.abs(localX) <= obstacle.halfW && Math.abs(localZ) <= obstacle.halfD) {
          visit(gz * this.cols + gx);
        }
      }
    }
  }

  /**
   * 网格 DDA（Amanatides & Woo）从观察者格步进到目标格，
   * 途经（不含起点与终点）blocked 格则不可见；终点格自身被挡仍算可见
   * （墙体本身要看得见，墙后才被遮）。
   * 对称性（A→B 与 B→A 判定必须一致，否则联机两端互相可见性不对称）：
   * 1. 以格中心为起点参数化，首条格界线恒在 0.5 格处、与方向无关，
   *    保证往返的格界穿越序列互为镜像；
   * 2. 擦墙角 tie（tMaxX 与 tMaxZ 浮点误差内相等）时双轴对角步进——
   *    只被“摸到”一个角点的正交邻居格跳过不查（纯擦角不算遮挡，permissive），
   *    对角格本身射线确实穿过，照查；旧实现在 tie 时只步进 Z 轴，
   *    会把仅擦角的格子当作途经格检查，导致往返判定不一致。
   */
  private hasLineOfSight(x0: number, z0: number, x1: number, z1: number): boolean {
    if (x0 === x1 && z0 === z1) return true;
    const dx = x1 - x0;
    const dz = z1 - z0;
    const stepX = dx > 0 ? 1 : -1;
    const stepZ = dz > 0 ? 1 : -1;
    // 沿轴每走一格对应的参数增量；另一轴垂直时不迈步（0.5 * Infinity 仍为 Infinity）。
    const tDeltaX = dx !== 0 ? Math.abs(1 / dx) : Infinity;
    const tDeltaZ = dz !== 0 ? Math.abs(1 / dz) : Infinity;
    let tMaxX = 0.5 * tDeltaX;
    let tMaxZ = 0.5 * tDeltaZ;

    let gx = x0;
    let gz = z0;
    // 步进到目标格为止；到达终点格即停，终点格不当作“途经”格检查。
    while (gx !== x1 || gz !== z1) {
      const tie = tMaxX !== Infinity && Math.abs(tMaxX - tMaxZ) <= LOS_EPSILON;
      if (tie && gx !== x1 && gz !== z1) {
        // 擦墙角：对角步进（tie 发生在终点角、某轴已到达时只步进未到的轴）。
        tMaxX += tDeltaX;
        tMaxZ += tDeltaZ;
        gx += stepX;
        gz += stepZ;
      } else if ((tMaxX < tMaxZ && gx !== x1) || gz === z1) {
        tMaxX += tDeltaX;
        gx += stepX;
      } else {
        tMaxZ += tDeltaZ;
        gz += stepZ;
      }
      if (gx === x1 && gz === z1) break;
      if (this.blockedGrid[gz * this.cols + gx] !== 0) return false;
    }
    return true;
  }
}

/** 两个世界角（atan2(dx, -dz) 约定）的最小角差，规整到 [-π, π]。 */
function angleDelta(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}
